
import React, { useEffect, useRef } from 'react';
import { PageSection, SiteTheme, HeroLayoutType } from '../../../types';
import { ArrowRight } from 'lucide-react';
import { HeroCard, HeroSplit, HeroFade, HeroBanner, HeroEditorial } from '../../store/hero/HeroVariants';

interface HeroSectionProps {
    section: PageSection;
    onAction: (action: string) => void;
    theme?: SiteTheme;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ section, onAction, theme }) => {
    const parallaxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (parallaxRef.current) {
            parallaxRef.current.style.willChange = 'transform';
        }

        let ticking = false;
        
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    if (parallaxRef.current) {
                        const y = window.scrollY;
                        // Use translate3d for hardware acceleration
                        parallaxRef.current.style.transform = `translate3d(0, ${y * 0.4}px, 0)`;
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prepare Custom Buttons
    const customButtons = (
        <div className={`flex flex-wrap gap-3 md:gap-4 ${['classic', 'soft', 'modern'].includes(theme?.styleMode || '') && section.heroLayout === 'card' && (!section.heroTextAlignment || section.heroTextAlignment === 'center') ? 'justify-center' : (section.heroTextAlignment === 'right' ? 'justify-end' : 'justify-start')}`}>
            {section.buttons?.map((btn, idx) => {
                // Determine styling logic
                const bgColor = btn.backgroundColor || (btn.primary ? (section.heroButtonColor || 'var(--color-primary)') : 'transparent');
                const borderColor = btn.backgroundColor ? btn.backgroundColor : (section.heroButtonColor || 'currentColor');
                const textColor = btn.textColor || (btn.primary ? (section.heroButtonTextColor || 'var(--color-bg-surface)') : (section.heroButtonColor || 'currentColor'));
                
                // If a manual background color is set, behave like a filled button regardless of primary status
                const isFilled = btn.primary || !!btn.backgroundColor;

                // Bold style override check via CSS vars is better, but here we do simple check to apply specific bold class if needed
                const isBoldMode = theme?.styleMode === 'bold';

                return (
                    <button 
                        key={idx}
                        onClick={() => onAction(btn.action)}
                        className={`px-6 py-3 md:px-8 md:py-4 font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 hover:scale-105 active:scale-95 ${
                            isFilled ? 'shadow-lg' : 'bg-transparent hover:bg-white/10'
                        } ${isBoldMode ? 'border-2 border-black shadow-[4px_4px_0px_black]' : ''}`}
                        style={{ 
                            // Use CSS variables for radius to prevent flash
                            borderRadius: isBoldMode ? '0px' : 'var(--radius-btn, 12px)',
                            backgroundColor: bgColor,
                            borderColor: isBoldMode ? 'black' : (isFilled ? bgColor : borderColor),
                            borderWidth: isBoldMode ? '2px' : (isFilled ? '0px' : '2px'),
                            borderStyle: 'solid',
                            color: textColor
                        }}
                    >
                        {btn.label} {btn.primary && <ArrowRight size={16} />}
                    </button>
                );
            })}
        </div>
    );

    // Prepare Custom Badge (Empty for Landing Page usually, or maybe "Nyhet")
    const customBadge = <></>; 

    // Helper to get safe position (default to 50 if null/undefined)
    const getPos = (val: number | undefined | null) => (val !== undefined && val !== null) ? val : 50;

    // Helper Props
    const commonProps = {
        title: section.title,
        text: section.text,
        imageUrl: section.imageUrl,
        mobileImageUrl: section.mobileImageUrl,
        theme: theme,
        titleColor: section.heroTitleColor,
        textColor: section.heroTextColor,
        titleShadowClass: '', // Handled by variants usually, or passed if needed
        titleSizeClass: '', // Variants calculate this, but we can override via prop if we mapped it 
        heightClass: section.heroHeight === 'full' ? 'min-h-screen' : 
                     section.heroHeight === 'large' ? 'min-h-[80vh]' : 
                     section.heroHeight === 'small' ? 'min-h-[50vh]' : 'min-h-[70vh]',
        onScrollToShop: () => {}, // Not used when customButtons passed
        customButtons: customButtons,
        customBadge: customBadge,
        
        // Pass style props down
        shadow: section.heroShadow,
        titleShadow: section.heroTitleShadow,
        textShadow: section.heroTextShadow,
        titleSize: section.heroTitleSize,
        subtitleSize: section.heroSubtitleSize,
        
        // Pass ref for parallax
        parallaxRef: parallaxRef,

        // Alignment & Position
        heroTextAlignment: section.heroTextAlignment || 'center',
        heroContentPosition: getPos(section.heroContentPosition),
        heroContentPositionY: getPos(section.heroContentPositionY),
        
        // Button props
        heroButtonColor: section.heroButtonColor,
        heroButtonTextColor: section.heroButtonTextColor,

        // NEW: Texture & Outline
        heroTitleTexture: section.heroTitleTexture,
        heroTitleOutlineWidth: section.heroTitleOutlineWidth,
        heroTitleOutlineColor: section.heroTitleOutlineColor,
        heroTextOutlineWidth: section.heroTextOutlineWidth,
        heroTextOutlineColor: section.heroTextOutlineColor,
    };

    // Explicitly map the size props since HeroVariants expect class strings or handled internally
    const getTitleSize = (size?: string) => {
        switch (size) {
            case 'sm': return 'text-2xl md:text-4xl lg:text-5xl';
            case 'md': return 'text-3xl md:text-5xl lg:text-6xl';
            case 'lg': return 'text-4xl md:text-6xl lg:text-7xl';
            case 'xl': return 'text-5xl md:text-7xl lg:text-8xl';
            case '2xl': return 'text-6xl md:text-8xl lg:text-9xl';
            default: return 'text-4xl md:text-6xl lg:text-7xl'; 
        }
    };
    
    const getShadowClass = (type?: string) => {
        switch (type) {
            case 'none': return '';
            case 'hard': return 'drop-shadow-[2px_2px_0_rgba(0,0,0,0.8)]';
            case 'strong': return 'drop-shadow-xl';
            case 'soft': return 'drop-shadow-md';
            case 'glow': return 'drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]';
            default: return 'drop-shadow-md';
        }
    };

    const finalProps = {
        ...commonProps,
        titleSizeClass: getTitleSize(section.heroTitleSize || 'lg'),
        titleShadowClass: getShadowClass(section.heroTitleShadow || section.heroShadow),
        textShadowClass: getShadowClass(section.heroTextShadow || section.heroShadow),
        subtitleSizeClass: section.heroSubtitleSize === 'sm' ? 'text-sm' : section.heroSubtitleSize === 'lg' ? 'text-xl' : 'text-base md:text-lg'
    };

    // Render based on Layout
    const layout = section.heroLayout || 'fade';

    switch (layout) {
        case 'split': return <HeroSplit {...finalProps} />;
        case 'fade': return <HeroFade {...finalProps} />;
        case 'banner': return <HeroBanner {...finalProps} />;
        case 'editorial': return <HeroEditorial {...finalProps} />;
        case 'card': 
        default: return <HeroCard {...finalProps} />;
    }
};
